import type { Reading } from "@spf/contracts";
import { Card } from "../Card.js";
import { cn } from "../../lib/cn.js";
import { formatUnit } from "../../lib/format.js";

/** The design's two spectral channels, kept as it draws them. Decoration. */
const LEFT = [40, 60, 80, 50, 90, 75, 35, 65, 85, 45, 55, 70, 30, 45, 60, 25, 40, 20] as const;
const RIGHT = [30, 50, 40, 65, 45, 55, 70, 35, 25, 40, 50, 30, 60, 35, 20, 45, 25, 15] as const;

/**
 * The design's Load Balance panel, showing what this hardware can actually
 * balance.
 *
 * The design splits load across a left and a right side, which is a
 * split-phase idea: two 120 V legs that can drift apart and want watching.
 * **This inverter is single phase.** There is no second leg, so a left and a
 * right figure here would not be waiting on a later slice -- they would be
 * describing an installation that does not exist, and no slice will ever
 * arrive to make them true.
 *
 * So the panel keeps its shape and states the split it does have: how much of
 * the inverter's rating the house is using, and how much headroom is left.
 * That is the same question -- am I close to a limit -- asked of the hardware
 * in front of us.
 */
export function LoadBalanceWidget({
  reading,
  onExpand,
  className,
}: {
  reading: Reading;
  onExpand?: () => void;
  className?: string;
}) {
  const used = reading.loadPercent;
  const spare = used === null ? null : Math.max(0, 100 - used);
  const lit = (bars: readonly number[], pct: number | null) =>
    pct === null ? 0 : Math.round((pct / 100) * bars.length);

  return (
    <Card
      onExpand={onExpand}
      title="Load Balance"
      className={cn("flex flex-col justify-between", className)}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="tabular text-2xl font-bold text-ink sm:text-3xl">
            {used === null ? "—" : `${used}%`}
          </p>
          <p className="mt-0.5 text-xs font-medium text-ink-muted">In use</p>
        </div>
        <div className="text-right">
          <p className="tabular text-2xl font-bold text-ink sm:text-3xl">
            {spare === null ? "—" : `${spare}%`}
          </p>
          <p className="mt-0.5 text-xs font-medium text-ink-muted">Headroom</p>
        </div>
      </div>

      <p className="mt-3 text-xs text-ink-muted">
        {formatUnit(reading.loadPowerW, "W", true)} drawn. Single phase, so there is one leg to
        watch rather than two to balance.
      </p>

      <div aria-hidden="true" className="mt-6">
        <div className="grid h-10 grid-cols-2 items-end gap-3">
          <Channel bars={LEFT} lit={lit(LEFT, used)} tone="bg-accent-2" />
          <Channel bars={RIGHT} lit={lit(RIGHT, spare)} tone="bg-accent" />
        </div>
      </div>
    </Card>
  );
}

function Channel({ bars, lit, tone }: { bars: readonly number[]; lit: number; tone: string }) {
  return (
    <div className="flex h-full items-end justify-between gap-[2px] rounded-lg border border-line bg-line/40 p-1.5">
      {bars.map((height, index) => (
        <div
          key={index}
          className={cn(
            "w-1 rounded-sm transition-all duration-300",
            index < lit ? tone : "bg-line-strong",
          )}
          style={{ height: `${height}%` }}
        />
      ))}
    </div>
  );
}
